import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { ApiClient } from '../utils/apiClient.js';
import LoadingSpinner from './LoadingSpinner.jsx';
import TimeRangeSelector from './TimeRangeSelector.jsx';
import QueueMetricsCard from './QueueMetricsCard.jsx';
import QueuePerformanceChart from './QueuePerformanceChart.jsx';
import QueueCostAnalysis from './QueueCostAnalysis.jsx';
import QueueHealthIndicator from './QueueHealthIndicator.jsx';

/**
 * Queue Monitoring Dashboard Component
 * Provides real-time queue performance monitoring with historical metrics visualization
 */
const QueueMonitoringDashboard = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [apiClient] = useState(() => new APIClient(getToken));
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [costs, setCosts] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [preferences, setPreferences] = useState({});
  
  // Real-time updates
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshTimer, setRefreshTimer] = useState(null);

  /**
   * Fetch dashboard data from API
   */
  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    try {
      setError(null);
      
      // Fetch main dashboard data
      const dashboardResponse = await apiClient.get(`/api/queue/dashboard?timeRange=${timeRange}`);
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.data);
      }
      
      // Fetch detailed metrics
      const metricsResponse = await apiClient.get(`/api/queue/dashboard/metrics?timeRange=${timeRange}`);
      if (metricsResponse.success) {
        setMetrics(metricsResponse.data);
      }
      
      // Fetch cost analysis
      const costsResponse = await apiClient.get(`/api/queue/dashboard/costs?timeRange=${timeRange}`);
      if (costsResponse.success) {
        setCosts(costsResponse.data);
      }
      
      // Fetch performance data
      const performanceResponse = await apiClient.get(`/api/queue/dashboard/performance?timeRange=${timeRange}`);
      if (performanceResponse.success) {
        setPerformance(performanceResponse.data);
      }
      
      // Fetch historical trends
      const trendsResponse = await apiClient.get(`/api/queue/dashboard/trends?timeRange=${timeRange}`);
      if (trendsResponse.success) {
        setTrends(trendsResponse.data);
      }
      
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [apiClient, timeRange]);

  /**
   * Fetch user preferences
   */
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/queue/dashboard/preferences');
      if (response.success) {
        setPreferences(response.data.preferences || {});
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  }, [apiClient]);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      const response = await apiClient.post('/api/queue/dashboard/preferences', {
        preferences: { ...preferences, ...newPreferences }
      });
      
      if (response.success) {
        setPreferences(prev => ({ ...prev, ...newPreferences }));
      }
    } catch (err) {
      console.error('Error updating preferences:', err);
    }
  }, [apiClient, preferences]);

  /**
   * Force refresh dashboard data
   */
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await apiClient.post('/api/queue/dashboard/refresh', {
        timeRange,
        forceRefresh: true
      });
      await fetchDashboardData(true);
    } catch (err) {
      console.error('Error refreshing dashboard:', err);
      setError(err.message || 'Failed to refresh dashboard');
    }
  }, [apiClient, timeRange, fetchDashboardData]);

  /**
   * Handle time range change
   */
  const handleTimeRangeChange = useCallback((newTimeRange) => {
    setTimeRange(newTimeRange);
    setLoading(true);
  }, []);

  /**
   * Toggle auto refresh
   */
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh(prev => !prev);
    updatePreferences({ autoRefresh: !autoRefresh });
  }, [autoRefresh, updatePreferences]);

  /**
   * Update refresh interval
   */
  const handleRefreshIntervalChange = useCallback((interval) => {
    setRefreshInterval(interval);
    updatePreferences({ refreshInterval: interval });
  }, [updatePreferences]);

  // Initial data fetch
  useEffect(() => {
    fetchPreferences();
    fetchDashboardData();
  }, [fetchPreferences, fetchDashboardData]);

  // Auto refresh setup
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const timer = setInterval(() => {
        fetchDashboardData();
      }, refreshInterval);
      
      setRefreshTimer(timer);
      
      return () => {
        if (timer) {
          clearInterval(timer);
        }
      };
    } else if (refreshTimer) {
      clearInterval(refreshTimer);
      setRefreshTimer(null);
    }
  }, [autoRefresh, refreshInterval, fetchDashboardData]);

  // Apply user preferences
  useEffect(() => {
    if (preferences.autoRefresh !== undefined) {
      setAutoRefresh(preferences.autoRefresh);
    }
    if (preferences.refreshInterval !== undefined) {
      setRefreshInterval(preferences.refreshInterval);
    }
  }, [preferences]);

  if (loading && !dashboardData) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading queue monitoring dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className={`text-6xl mb-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`}>⚠️</div>
          <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Dashboard Error
          </h2>
          <p className={`text-lg mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            {error}
          </p>
          <button
            onClick={handleRefresh}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Queue Monitoring Dashboard
              </h1>
              <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Real-time queue performance monitoring and analytics
              </p>
            </div>
            
            <div className="mt-4 sm:mt-0 flex items-center space-x-4">
              {/* Last Updated */}
              {lastUpdated && (
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              
              {/* Auto Refresh Toggle */}
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={toggleAutoRefresh}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoRefresh ? 'bg-teal-600' : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoRefresh ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
                <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Auto Refresh
                </span>
              </label>
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700'
                } text-white`}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
          
          {/* Time Range Selector */}
          <div className="mt-6">
            <TimeRangeSelector
              value={timeRange}
              onChange={handleTimeRangeChange}
              options={[
                { value: '1h', label: '1 Hour' },
                { value: '6h', label: '6 Hours' },
                { value: '24h', label: '24 Hours' },
                { value: '7d', label: '7 Days' },
                { value: '30d', label: '30 Days' }
              ]}
            />
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="space-y-8">
          {/* Queue Health Overview */}
          {dashboardData && (
            <QueueHealthIndicator
              data={dashboardData}
              theme={theme}
            />
          )}
          
          {/* Metrics Cards */}
          {metrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <QueueMetricsCard
                title="Total Messages"
                value={metrics.metrics?.totalMessages || 0}
                change={metrics.metrics?.messageChange || 0}
                theme={theme}
                icon="📊"
              />
              <QueueMetricsCard
                title="Processing Time"
                value={`${metrics.metrics?.avgProcessingTime || 0}ms`}
                change={metrics.metrics?.processingTimeChange || 0}
                theme={theme}
                icon="⏱️"
              />
              <QueueMetricsCard
                title="Success Rate"
                value={`${((metrics.metrics?.successRate || 0) * 100).toFixed(1)}%`}
                change={metrics.metrics?.successRateChange || 0}
                theme={theme}
                icon="✅"
              />
              <QueueMetricsCard
                title="Error Rate"
                value={`${((metrics.metrics?.errorRate || 0) * 100).toFixed(2)}%`}
                change={metrics.metrics?.errorRateChange || 0}
                theme={theme}
                icon="❌"
                isError={true}
              />
            </div>
          )}
          
          {/* Performance Charts */}
          {trends && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <QueuePerformanceChart
                title="Message Throughput"
                data={trends.trends?.throughput || []}
                theme={theme}
                type="line"
              />
              <QueuePerformanceChart
                title="Processing Times"
                data={trends.trends?.processingTimes || []}
                theme={theme}
                type="area"
              />
            </div>
          )}
          
          {/* Cost Analysis */}
          {costs && (
            <QueueCostAnalysis
              data={costs.costSummary}
              theme={theme}
              timeRange={timeRange}
            />
          )}
          
          {/* Performance Summary */}
          {performance && (
            <div className={`${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } rounded-lg shadow border p-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Performance Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Queue Health
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(performance.performanceSummary?.queueHealth || {}).map(([queue, health]) => (
                      <div key={queue} className="flex items-center justify-between">
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {queue}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          health === 'healthy' ? 'bg-green-100 text-green-800' :
                          health === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {health}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Optimization Recommendations
                  </h4>
                  <div className="space-y-2">
                    {(performance.performanceSummary?.recommendations || []).map((rec, index) => (
                      <div key={index} className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        • {rec}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Key Metrics
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Avg Latency
                      </span>
                      <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {performance.performanceSummary?.avgLatency || 0}ms
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Throughput
                      </span>
                      <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {performance.performanceSummary?.throughput || 0}/min
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueueMonitoringDashboard;
